import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { Customer } from './core/enums/customer.enum';
import { MaintenanceService } from './shared/services/maintenance.service';
import { DebugState } from './shared/services/debug.service';

interface Config {
    apiUrl: string;
    apiV3Url: string;
    addonsUrl: string;
    addonsV3Url: string;
    ecmrUrl: string;
    customer?: Customer;
    twoFactorEnabled?: boolean | string;
    maintenance?: boolean | string;
    languageSwitcher?: boolean;
    production?: boolean | string;
    environment: string;
    oAuthIssuer?: string;
    oAuthClientId?: string;
    oAuthForceHttps?: boolean | string;
    managementOAuthAuthority?: string;
    managementOAuthClientId?: string;
    managementOAuthRoleRead?: string;
    managementOAuthRoleWrite?: string;
    managementApiUrl?: string;
}

export interface DcConfigData {
    env: string;
    dc: string;
    orgSiteKey: string;
    applicableName: string;
    gln: string;
    warehouseName: string;
    street: string;
    zip: string;
    city: string;
}

export interface IAppConfig {
    apiUrl: string;
    apiV3Url: string;
    addonsUrl: string;
    addonsV3Url: string;
    ecmrUrl: string;
    load: () => Promise<void>;
    dcData: DcConfigData[];
}

@Injectable({
    providedIn: 'root',
})
export class AppConfig implements IAppConfig {
    public apiUrl: string;
    public apiV3Url: string;
    public addonsUrl: string;
    public addonsV3Url: string;
    public ecmrUrl: string;
    public customer?: Customer;
    public twoFactorEnabled?: boolean;
    public maintenance: boolean;
    public languageSwitcher?: boolean = false;
    public production?: boolean = false;
    public environment: string;
    public dcData: DcConfigData[] = [];
    public oAuthIssuer?: string;
    public oAuthClientId?: string;
    public oAuthForceHttps?: boolean = true;
    public managementOAuthAuthority?: string;
    public managementOAuthClientId?: string;
    public managementOAuthRoleRead?: string;
    public managementOAuthRoleWrite?: string;
    public managementApiUrl?: string;

    private readonly DEBUG_STORAGE_KEY = 'debugState';

    constructor(
        private readonly http: HttpClient,
        private maintenanceService: MaintenanceService
    ) {}

    public async load(): Promise<void> {
        let savedEnvironment: 'production' | 'test' = environment.production
            ? 'production'
            : 'test';
        try {
            const savedDebugState = localStorage.getItem(
                this.DEBUG_STORAGE_KEY
            );
            if (savedDebugState) {
                const parsedDebugState = JSON.parse(
                    savedDebugState
                ) as Partial<DebugState>;
                if (
                    parsedDebugState.environment === 'production' ||
                    parsedDebugState.environment === 'test'
                ) {
                    savedEnvironment = parsedDebugState.environment;
                }
            }
        } catch (e) {
            console.error(
                'AppConfig: Error reading debug state from localStorage',
                e
            );
        }

        // Temporarily set these values (will be updated after loading config.json)
        this.environment = savedEnvironment;
        this.production = savedEnvironment === 'production';

        const config = await lastValueFrom(
            this.http.get<Config>(
                `assets/config.json?v=${new Date().getTime()}`
            )
        );

        // Process production setting first
        if (config.production === '${PRODUCTION}') {
            config.production = environment.production;
        } else {
            config.production =
                String(config.production).toLowerCase() === 'true';
        }
        this.production = config.production;

        // Handle environment based on production setting
        if (config.production) {
            // If production is true, use config.environment
            if (config.environment === '${ENVIRONMENT}') {
                config.environment = environment.environment;
            }
            this.environment = config.environment;
        } else {
            // If production is false, use saved environment from localStorage
            this.environment = savedEnvironment;
        }

        // Process other config settings
        if (config.apiUrl === '${API_URL}') {
            config.apiUrl = environment.apiUrl;
        }
        this.apiUrl = config.apiUrl;

        if (config.apiV3Url === '${API_URL_V3}') {
            config.apiV3Url = environment.apiV3Url;
        }
        console.log('api v3 url', config.apiV3Url);
        this.apiV3Url = config.apiV3Url;

        if (config.addonsUrl === '${ADDONS_URL}') {
            config.addonsUrl = environment.addonsUrl;
        }
        this.addonsUrl = config.addonsUrl;

        if (config.addonsV3Url === '${ADDONS_V3_URL}') {
            config.addonsV3Url = environment.addonsV3Url;
        }
        this.addonsV3Url = config.addonsV3Url;

        if (config.ecmrUrl === '${ECMR_URL}') {
            config.ecmrUrl = environment.ecmrUrl;
        }
        this.ecmrUrl = config.ecmrUrl;

        config.twoFactorEnabled =
            config.twoFactorEnabled === '${TWO_FACTOR_ENABLED}'
                ? environment.twoFactorEnabled
                : String(config.twoFactorEnabled).toLowerCase() === 'true';
        this.twoFactorEnabled = config.twoFactorEnabled;

        this.languageSwitcher =
            typeof config.languageSwitcher === 'boolean'
                ? config.languageSwitcher
                : environment.languageSwitcher;

        if (config.oAuthIssuer === '${OAUTH_ISSUER}') {
            config.oAuthIssuer = environment.oAuthIssuer;
        }
        this.oAuthIssuer = config.oAuthIssuer;

        if (config.oAuthClientId === '${OAUTH_CLIENT_ID}') {
            config.oAuthClientId = environment.oAuthClientId;
        }
        this.oAuthClientId = config.oAuthClientId;

        if (config.oAuthForceHttps === '${OAUTH_FORCE_HTTPS}') {
            config.oAuthForceHttps = environment.oAuthForceHttps;
        } else {
            config.oAuthForceHttps = config.oAuthForceHttps == 'true';
        }
        this.oAuthForceHttps = config.oAuthForceHttps;

        if (
            config.managementOAuthAuthority === '${MANAGEMENT_OAUTH_AUTHORITY}'
        ) {
            config.managementOAuthAuthority =
                environment.managementOAuthAuthority;
        }
        this.managementOAuthAuthority = config.managementOAuthAuthority;

        if (
            config.managementOAuthClientId === '${MANAGEMENT_OAUTH_CLIENT_ID}'
        ) {
            config.managementOAuthClientId =
                environment.managementOAuthClientId;
        }
        this.managementOAuthClientId = config.managementOAuthClientId;

        if (
            config.managementOAuthRoleRead === '${MANAGEMENT_OAUTH_ROLE_WRITE}'
        ) {
            config.managementOAuthRoleRead =
                environment.managementOAuthRoleRead;
        }
        this.managementOAuthRoleRead = config.managementOAuthRoleRead;

        if (
            config.managementOAuthRoleWrite === '${MANAGEMENT_OAUTH_ROLE_WRITE}'
        ) {
            config.managementOAuthRoleWrite =
                environment.managementOAuthRoleWrite;
        }
        this.managementOAuthRoleWrite = config.managementOAuthRoleWrite;

        if (config.managementApiUrl === '${MANAGEMENT_API_URL}') {
            config.managementApiUrl = environment.managementApiUrl;
        }
        this.managementApiUrl = config.managementApiUrl;

        config.maintenance =
            config.maintenance === '${MAINTENANCE}'
                ? environment.maintenance
                : String(config.maintenance).toLowerCase() === 'true';
        this.maintenance = config.maintenance;
        this.maintenanceService.setMaintenanceMode(this.maintenance);

        try {
            const dcDataResult = await lastValueFrom(
                this.http.get<DcConfigData[]>(
                    `assets/dc-data.json?v=${new Date().getTime()}`
                )
            );
            this.dcData = dcDataResult || [];
        } catch (error) {
            console.error('AppConfig: Error loading dc-data.json:', error);
            this.dcData = [];
        }
    }
}

export function initConfig(config: AppConfig): () => Promise<void> {
    return () => config.load();
}
